import React from 'react';
import { CalendarDays, CreditCard, FileText, Mail } from 'lucide-react';
import {
  Box,
  Checkbox,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

export interface InviteActionsState {
  scheduleAppointment: boolean;
  attachQuestionnaire: boolean;
  requestPayment: boolean;
}

interface InviteOnlyStepProps {
  clientFirstName: string;
  clientEmail: string;
  inviteMessage: string;
  onInviteMessageChange: (value: string) => void;
  actions: InviteActionsState;
  onActionChange: (action: keyof InviteActionsState, checked: boolean) => void;
  onScheduleAppointmentClick?: () => void;
  onAttachQuestionnaireClick?: () => void;
  onRequestPaymentClick?: () => void;
}

const InviteOnlyStep: React.FC<InviteOnlyStepProps> = ({
  clientFirstName,
  clientEmail,
  inviteMessage,
  onInviteMessageChange,
  actions,
  onActionChange,
  onScheduleAppointmentClick,
  onAttachQuestionnaireClick,
  onRequestPaymentClick,
}) => {
  const safeFirstName = clientFirstName?.trim() || 'there';
  const safeEmail = clientEmail?.trim() || 'client@email.com';

  const previewMessage = `Dear ${safeFirstName},
Welcome! Please complete your onboarding using the secure link below.

Regards,
CalorieScience Team`;

  const actionBoxSx = {
    width: {
      xs: '100%',
      xl: '328.6667px',
    },
    height: '56px',
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    pl: '16px',
    pr: '16px',
    borderRadius: '8px',
    borderWidth: '1px',
    borderStyle: 'solid',
    backgroundColor: '#FFFFFF',
    cursor: 'pointer',
    transition: 'border-color 0.2s ease',
  } as const;

  return (
    <Stack spacing={3}>
      <Box
        sx={{
          maxWidth: '560px',
          border: '1px solid #DCE7EE',
          borderRadius: '10px',
          backgroundColor: '#F8FCFF',
          px: 1.4,
          py: 1.1,
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.9,
        }}
      >
        <Mail size={16} color="#20AEDC" />
        <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '0.83rem', color: '#344054' }}>
          Invite channel: <Box component="span" sx={{ fontWeight: 600, color: '#0F172A' }}>Email</Box>
        </Typography>
      </Box>

      <Box sx={{ maxWidth: '560px' }}>
        <Typography
          sx={{
            mb: 1,
            fontFamily: 'Inter, sans-serif',
            fontSize: '14px',
            fontWeight: 500,
            color: '#1D2B2D',
          }}
        >
          Message Preview
        </Typography>

        <Box
          sx={{
            border: '1px solid #20AEDC',
            borderRadius: '12px',
            backgroundColor: '#FFFFFF',
            p: '12px',
          }}
        >
          <Box
            sx={{
              mb: '8px',
              pb: '8px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              borderBottom: '1px solid rgba(225, 225, 225, 0.7)',
              color: '#6B7280',
              fontFamily: 'Inter, sans-serif',
              fontSize: '14px',
            }}
          >
            <Mail size={16} />
            <span>{`To: ${safeEmail}`}</span>
          </Box>

          <TextField
            multiline
            minRows={5}
            fullWidth
            value={inviteMessage}
            onChange={(event) => onInviteMessageChange(event.target.value)}
            placeholder={previewMessage}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '8px',
                bgcolor: '#FFFFFF',
                alignItems: 'flex-start',
                '& fieldset': { borderColor: '#E1E1E1' },
              },
              '& .MuiInputBase-input': {
                fontFamily: 'Inter, sans-serif',
                fontSize: '14px',
                lineHeight: '24px',
                color: '#6B7280',
                whiteSpace: 'pre-line',
              },
              '& .MuiInputBase-input::placeholder': {
                color: '#98A2B3',
                opacity: 1,
                whiteSpace: 'pre-line',
              },
            }}
          />
        </Box>
      </Box>

      <Stack spacing={1.5}>
        <Typography
          sx={{
            fontFamily: 'Inter, sans-serif',
            fontSize: '16px',
            fontWeight: 600,
            color: '#1D2B2D',
          }}
        >
          You can perform one or more actions below.
        </Typography>

        <Box
          sx={{
            maxWidth: '1010px',
            display: 'grid',
            gap: '12px',
            gridTemplateColumns: {
              xs: '1fr',
              md: '1fr 1fr',
              xl: '328.6667px 328.6667px 328.6667px',
            },
          }}
        >
          <Box
            component="button"
            type="button"
            data-analytics-event="Add Appointment Step 3 Clicked"
            data-analytics-id="client_onboarding_add_appointment_step_3_button"
            data-analytics-label="add_appointment_step_3"
            data-analytics-section="client_onboarding_step_3"
            onClick={() => {
              if (onScheduleAppointmentClick) {
                onScheduleAppointmentClick();
                return;
              }
              onActionChange('scheduleAppointment', !actions.scheduleAppointment);
            }}
            sx={{
              ...actionBoxSx,
              borderColor: actions.scheduleAppointment ? '#20AEDC' : '#E1E1E1',
              outline: 'none',
              textAlign: 'left',
            }}
          >
            <Checkbox
              id="invite-action-schedule-appointment"
              checked={actions.scheduleAppointment}
              onClick={(event) => event.stopPropagation()}
              onChange={(_, checked) => {
                if (onScheduleAppointmentClick && checked) {
                  onScheduleAppointmentClick();
                  return;
                }
                onActionChange('scheduleAppointment', checked);
              }}
              size="small"
              sx={{
                p: 0,
                color: '#D1D5DB',
                '&.Mui-checked': {
                  color: '#20AEDC',
                },
              }}
            />
            <CalendarDays size={16} color="#6B7280" />
            <Typography
              sx={{
                whiteSpace: 'nowrap',
                fontFamily: 'Inter, sans-serif',
                fontSize: '14px',
                color: '#1D2B2D',
              }}
            >
              Schedule an Appointment
            </Typography>
          </Box>

          <Box
            component="button"
            type="button"
            data-analytics-event="Attach Questionnaire Step 3 Clicked"
            data-analytics-id="client_onboarding_attach_questionnaire_step_3_button"
            data-analytics-label="attach_questionnaire_step_3"
            data-analytics-section="client_onboarding_step_3"
            onClick={() => {
              if (onAttachQuestionnaireClick) {
                onAttachQuestionnaireClick();
                return;
              }
              onActionChange('attachQuestionnaire', !actions.attachQuestionnaire);
            }}
            sx={{
              ...actionBoxSx,
              borderColor: actions.attachQuestionnaire ? '#20AEDC' : '#E1E1E1',
              outline: 'none',
              textAlign: 'left',
            }}
          >
            <Checkbox
              id="invite-action-attach-questionnaire"
              checked={actions.attachQuestionnaire}
              onClick={(event) => event.stopPropagation()}
              onChange={(_, checked) => {
                if (onAttachQuestionnaireClick && checked) {
                  onAttachQuestionnaireClick();
                  return;
                }
                onActionChange('attachQuestionnaire', checked);
              }}
              size="small"
              sx={{
                p: 0,
                color: '#D1D5DB',
                '&.Mui-checked': {
                  color: '#20AEDC',
                },
              }}
            />
            <FileText size={16} color="#6B7280" />
            <Typography
              sx={{
                whiteSpace: 'nowrap',
                fontFamily: 'Inter, sans-serif',
                fontSize: '14px',
                color: '#1D2B2D',
              }}
            >
              Attach Questionnaire
            </Typography>
          </Box>

          <Box
            component="button"
            type="button"
            data-analytics-event="Add Invoice Step 3 Clicked"
            data-analytics-id="client_onboarding_add_invoice_step_3_button"
            data-analytics-label="add_invoice_step_3"
            data-analytics-section="client_onboarding_step_3"
            onClick={() => {
              if (onRequestPaymentClick) {
                onRequestPaymentClick();
                return;
              }
              onActionChange('requestPayment', !actions.requestPayment);
            }}
            sx={{
              ...actionBoxSx,
              borderColor: actions.requestPayment ? '#20AEDC' : '#E1E1E1',
              outline: 'none',
              textAlign: 'left',
            }}
          >
            <Checkbox
              id="invite-action-request-payment"
              checked={actions.requestPayment}
              onClick={(event) => event.stopPropagation()}
              onChange={(_, checked) => {
                if (onRequestPaymentClick && checked) {
                  onRequestPaymentClick();
                  return;
                }
                onActionChange('requestPayment', checked);
              }}
              size="small"
              sx={{
                p: 0,
                color: '#D1D5DB',
                '&.Mui-checked': {
                  color: '#20AEDC',
                },
              }}
            />
            <CreditCard size={16} color="#6B7280" />
            <Typography
              sx={{
                whiteSpace: 'nowrap',
                fontFamily: 'Inter, sans-serif',
                fontSize: '14px',
                color: '#1D2B2D',
              }}
            >
              Request Payment
            </Typography>
          </Box>
        </Box>
      </Stack>
    </Stack>
  );
};

export default InviteOnlyStep;
